import { useEffect, useState } from 'react';
import type {
  BrancheResponse,
  ComparisonCell,
  ManualCorrectionDto,
  ComparisonResponse,
  ComparisonRow,
  CoverageEntryDto,
  ReportSectionDto,
  ReportSynthesisResponse,
  RunChangesResponse,
  SourcesResponse,
  SynthesisClaimDto,
  ValidationFindingDto,
} from '@swg/contracts';
import {
  AVAILABILITY_LABELS,
  CLASSIFICATION_LABELS,
  COMPARABILITY_STATUS_LABELS,
  CONNECTOR_RUN_STATUS_LABELS,
  METRIC_CATEGORY_LABELS,
  PROVENANCE_TIER_LABELS,
  SOURCE_FAMILY_LABELS,
  formatCellText,
} from '@swg/domain';
import {
  ApiError,
  createCorrection,
  fetchAuthMe,
  fetchBranche,
  fetchComparison,
  fetchCorrections,
  fetchRunChanges,
  fetchSources,
  fetchSynthesis,
  withdrawCorrection,
} from '../api.js';
import { theme } from '../theme.js';

/**
 * Deterministische rapportviewer (M19 / US07A, uitgebreid in P11):
 * marktkader, vergelijkingstabellen per categorie, dekking per concurrent,
 * bronnen, actualiteit, validatiebevindingen en beperkingen. Geen
 * AI-conclusies in de tabellen; alles komt rechtstreeks uit de opgeslagen
 * observaties. Ontbrekende data is "Niet beschikbaar", nooit 0, feiten en
 * schattingen zijn altijd gelabeld, en een AI-waarde draagt haar label in de
 * celtekst zelf (`displayText` komt van de server, zodat viewer en export
 * dezelfde string tonen).
 */

const numberFormat = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 2 });

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('nl-NL', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

const CLASSIFICATION_COLOR: Record<string, { bg: string; text: string }> = {
  FACT: { bg: '#E2F2E7', text: '#1E7A44' },
  ESTIMATE: { bg: theme.color.accentBg, text: theme.color.primary },
  INFERENCE: { bg: '#F0E7F6', text: '#6B3FA0' },
};

function ClassificationChip({ classification }: { classification: string | null }) {
  if (!classification) return null;
  const color = CLASSIFICATION_COLOR[classification] ?? {
    bg: theme.color.accentBg,
    text: theme.color.primary,
  };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 7px',
        borderRadius: 9,
        fontSize: 10.5,
        fontWeight: 600,
        background: color.bg,
        color: color.text,
        whiteSpace: 'nowrap',
      }}
    >
      {CLASSIFICATION_LABELS[classification as keyof typeof CLASSIFICATION_LABELS] ??
        classification}
    </span>
  );
}

const RELIABILITY_COLOR_MEDIUM = {
  bg: theme.color.warnBg,
  text: theme.color.warnText,
  dot: '#C9861F',
};
const RELIABILITY_COLOR: Record<string, { bg: string; text: string; dot: string }> = {
  high: { bg: '#E2F2E7', text: '#1E7A44', dot: '#2E9E5B' },
  medium: RELIABILITY_COLOR_MEDIUM,
  low: { bg: '#FBE6E6', text: '#B02A2A', dot: '#D14343' },
};

function ReliabilityChip({ cell }: { cell: ComparisonCell }) {
  if (!cell.reliability) return null;
  const c = RELIABILITY_COLOR[cell.reliability.label] ?? RELIABILITY_COLOR_MEDIUM;
  return (
    <span
      title={cell.reliability.explanation}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '1px 7px',
        borderRadius: 9,
        fontSize: 10.5,
        fontWeight: 600,
        background: c.bg,
        color: c.text,
        whiteSpace: 'nowrap',
        cursor: 'help',
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: c.dot,
          display: 'inline-block',
        }}
      />
      {cell.reliability.labelNl}
    </span>
  );
}

function CorrectionChip({ cell }: { cell: ComparisonCell }) {
  if (!cell.correction) return null;
  const protectedNote = cell.correction.protectedFromRegeneration
    ? ' (beschermd tegen regeneratie)'
    : '';
  return (
    <span
      title={`Handmatig gecorrigeerd: ${cell.correction.reason}${protectedNote}`}
      style={{
        display: 'inline-block',
        padding: '1px 7px',
        borderRadius: 9,
        fontSize: 10.5,
        fontWeight: 600,
        background: '#FBEED6',
        color: '#8A5A00',
        whiteSpace: 'nowrap',
      }}
    >
      ✎ Handmatig
    </span>
  );
}

/**
 * Herkomstniveau (tier A/B/C). Alleen tier C krijgt een chip: bij A en B zou een
 * extra label per cel de tabel onleesbaar maken zonder iets toe te voegen, want
 * dat is het normale geval. Het AI-label staat daarnaast al in de celtekst
 * (`displayText`) — de chip is de opmaakversie, de tekst is de versie die een
 * kopie naar een deck of e-mail overleeft.
 */
function TierChip({ cell }: { cell: ComparisonCell }) {
  if (cell.provenanceTier !== 'AI_GENERATED') return null;
  return (
    <span
      title="Door AI gegenereerd of afgeleid (tier C). Controleer de bron voordat u hierop handelt."
      style={{
        display: 'inline-block',
        padding: '1px 7px',
        borderRadius: 9,
        fontSize: 10.5,
        fontWeight: 600,
        background: '#F0E7F6',
        color: '#6B3FA0',
        whiteSpace: 'nowrap',
        cursor: 'help',
      }}
    >
      {PROVENANCE_TIER_LABELS.AI_GENERATED}
    </span>
  );
}

function sourceLabel(cell: ComparisonCell): string | null {
  if (!cell.source) return null;
  const family = cell.source.family
    ? (SOURCE_FAMILY_LABELS[cell.source.family] ?? cell.source.family)
    : null;
  return family;
}

function ComparisonTable({
  rows,
  subjects,
  coverage,
  canCorrect,
  editing,
  onEdit,
  runId,
  onCorrected,
}: {
  rows: ComparisonRow[];
  subjects: ComparisonResponse['subjects'];
  coverage: CoverageEntryDto[];
  /** Mag deze gebruiker corrigeren? Kijkers zien de knop niet. */
  canCorrect: boolean;
  /** `metricCode|subjectKey` van de cel die nu wordt gecorrigeerd. */
  editing: string | null;
  onEdit: (key: string | null) => void;
  runId: string;
  onCorrected: () => void;
}) {
  const coverageByKey = new Map(coverage.map((c) => [c.subjectKey, c]));
  const cellStyle = {
    padding: '10px 12px',
    borderBottom: `1px solid ${theme.color.border}`,
    fontSize: 13,
    verticalAlign: 'top' as const,
  };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th
              style={{ ...cellStyle, textAlign: 'left', color: theme.color.muted, fontWeight: 600 }}
            >
              Metriek
            </th>
            {subjects.map((s) => (
              <th
                key={s.key}
                style={{ ...cellStyle, textAlign: 'left', fontWeight: s.isOwnCompany ? 700 : 600 }}
              >
                {s.name}
                {s.isOwnCompany && (
                  <span style={{ color: theme.color.muted, fontWeight: 400 }}> (eigen)</span>
                )}
                {/* Dekking per subject (P11): een concurrent met drie gevulde
                    velden is niet met het eigen bedrijf te vergelijken, en dat
                    hoort boven de kolom te staan en niet in een voetnoot. */}
                {coverageByKey.get(s.key) && (
                  <div style={{ fontSize: 11, fontWeight: 400, color: theme.color.muted }}>
                    {coverageByKey.get(s.key)!.labelNl}
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <RowGroup
              key={row.metricCode}
              row={row}
              subjects={subjects}
              cellStyle={cellStyle}
              canCorrect={canCorrect}
              editing={editing}
              onEdit={onEdit}
              runId={runId}
              onCorrected={onCorrected}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RowGroup({
  row,
  subjects,
  cellStyle,
  canCorrect,
  editing,
  onEdit,
  runId,
  onCorrected,
}: {
  row: ComparisonRow;
  subjects: ComparisonResponse['subjects'];
  cellStyle: React.CSSProperties;
  canCorrect: boolean;
  editing: string | null;
  onEdit: (key: string | null) => void;
  runId: string;
  onCorrected: () => void;
}) {
  const cellsByKey = new Map(row.cells.map((c) => [c.subjectKey, c]));
  const hasWarnings = row.comparability.warnings.length > 0;
  const editingSubject = editing?.startsWith(`${row.metricCode}|`)
    ? subjects.find((s) => s.key === editing.slice(row.metricCode.length + 1))
    : undefined;
  return (
    <>
      <tr>
        <td style={cellStyle}>
          <div style={{ fontWeight: 600 }}>{row.nameNl}</div>
          <div style={{ fontSize: 11.5, color: theme.color.muted }}>
            {row.unit ? `${row.unit} · ` : ''}
            {COMPARABILITY_STATUS_LABELS[row.comparability.status]}
          </div>
          {/* "3 van 8 bekend" (P11): op zo'n rij mag geen rangorde worden
              uitgesproken, en de lezer moet kunnen zien waarom niet. */}
          {row.completeness.labelNl && (
            <div style={{ fontSize: 11.5, color: theme.color.warnText }}>
              {row.completeness.labelNl} — geen rangorde op deze dimensie
            </div>
          )}
        </td>
        {subjects.map((s) => {
          const cell = cellsByKey.get(s.key);
          if (!cell)
            return (
              <td key={s.key} style={cellStyle}>
                —
              </td>
            );
          const unavailable = cell.availability !== 'AVAILABLE';
          const src = sourceLabel(cell);
          return (
            <td key={s.key} style={cellStyle}>
              <div style={{ color: unavailable ? theme.color.subtle : theme.color.text }}>
                {cell.displayText}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 3,
                  flexWrap: 'wrap',
                }}
              >
                <ClassificationChip classification={unavailable ? null : cell.classification} />
                {!unavailable && <TierChip cell={cell} />}
                {!unavailable && <ReliabilityChip cell={cell} />}
                {!unavailable && <CorrectionChip cell={cell} />}
                {src && !unavailable && !cell.correction && (
                  <span style={{ fontSize: 10.5, color: theme.color.muted }}>{src}</span>
                )}
                {canCorrect && (
                  <button
                    type="button"
                    title="Deze waarde handmatig corrigeren"
                    onClick={() =>
                      onEdit(
                        editing === `${row.metricCode}|${s.key}`
                          ? null
                          : `${row.metricCode}|${s.key}`,
                      )
                    }
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      fontSize: 12,
                      color: theme.color.muted,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    ✎
                  </button>
                )}
              </div>
            </td>
          );
        })}
      </tr>
      {hasWarnings && (
        <tr>
          <td
            colSpan={subjects.length + 1}
            style={{
              padding: '6px 12px 10px',
              borderBottom: `1px solid ${theme.color.border}`,
              background: theme.color.warnBg,
              fontSize: 12,
              color: theme.color.warnText,
            }}
          >
            {row.comparability.warnings.map((w) => (
              <div key={w.code}>⚠ {w.message}</div>
            ))}
          </td>
        </tr>
      )}
      {/* Het correctieformulier komt onder de rij te staan in plaats van in de
          cel: een formulier in een tabelcel maakt de kolom onbruikbaar breed. */}
      {editingSubject && (
        <tr>
          <td colSpan={subjects.length + 1} style={{ padding: '0 12px 10px' }}>
            <CorrectionForm
              runId={runId}
              row={row}
              subject={editingSubject}
              cell={cellsByKey.get(editingSubject.key)}
              onDone={() => {
                onEdit(null);
                onCorrected();
              }}
              onCancel={() => onEdit(null)}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Marktkader (P11, PLAN.md §11).
 *
 * De CBS-brancheinformatie kwam tot nu toe **uitsluitend** in wizardstap 2 en
 * verscheen nergens in het rapport of de export: de analist bevestigde de
 * branchecijfers halverwege de flow en zag ze daarna nooit meer terug. Dat is
 * precies de context waarin de hele vergelijking gelezen moet worden — hoeveel
 * bedrijven zitten in deze SBI, groeit of krimpt de markt — dus staat het nu
 * bovenaan het rapport, vóór de concurrentvergelijking.
 *
 * Bij elk cijfer staat waar het over gaat: de SBI-code, het niveau waarop CBS
 * matchte, de periode en de tabel-ID. Een cijfer over "sectie C — industrie" is
 * iets anders dan een cijfer over de eigen 4-cijferige code, en wie dat verschil
 * niet ziet vergelijkt zijn bedrijf met de verkeerde markt.
 */
function MarketFrameCard({
  branche,
  card,
}: {
  branche: BrancheResponse;
  card: React.CSSProperties;
}) {
  const available = branche.statistics.filter((st) => st.availability === 'AVAILABLE');
  const coarser = branche.statistics.filter(
    (st) => st.matchLevel === 'COARSER' || st.matchLevel === 'UNRELATED',
  );
  const missing = branche.statistics.length - available.length;

  return (
    <div style={{ ...card, borderLeft: `4px solid ${theme.color.primary}` }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          marginBottom: 6,
          flexWrap: 'wrap',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Marktkader</h2>
        <span style={{ fontSize: 11.5, color: theme.color.muted }}>
          {branche.sbi
            ? `SBI ${branche.sbi.code}${branche.sbi.title ? ` — ${branche.sbi.title}` : ''} · herkomst ${
                branche.sbi.source === 'KVK'
                  ? 'KVK (feit)'
                  : branche.sbi.source === 'MANUAL'
                    ? 'handmatig gekozen'
                    : 'AI-aanname'
              }`
            : 'SBI-code niet vastgesteld'}
        </span>
      </div>

      {branche.statistics.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13.5, color: theme.color.muted }}>
          {branche.failureSummary ??
            'Er zijn geen branchecijfers opgehaald voor deze uitvoering. De vergelijking hieronder staat daarmee zonder marktcontext.'}
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 4 }}>
            <thead>
              <tr>
                {['Kenmerk', 'Waarde', 'Periode', 'Gematchte SBI', 'Tabel'].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: '7px 12px',
                      borderBottom: `1px solid ${theme.color.border}`,
                      fontSize: 12,
                      color: theme.color.muted,
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {branche.statistics.map((st) => {
                const td = {
                  padding: '7px 12px',
                  borderBottom: `1px solid ${theme.color.border}`,
                  fontSize: 13,
                  verticalAlign: 'top' as const,
                };
                const unavailable = st.availability !== 'AVAILABLE';
                return (
                  <tr key={st.metricCode}>
                    <td style={td}>{st.nameNl}</td>
                    <td
                      style={{ ...td, color: unavailable ? theme.color.subtle : theme.color.text }}
                    >
                      {unavailable
                        ? (AVAILABILITY_LABELS[st.availability] ?? 'Niet beschikbaar')
                        : `${st.valueNumber === null ? AVAILABILITY_LABELS.NOT_AVAILABLE : numberFormat.format(st.valueNumber)}${st.unit ? ` ${st.unit}` : ''}`}
                    </td>
                    <td style={td}>{st.periodLabel ?? '—'}</td>
                    <td style={td}>
                      {st.matchedSbiCode ?? '—'}
                      {st.matchLevel === 'COARSER' && (
                        <span style={{ color: theme.color.warnText, fontSize: 11.5 }}>
                          {' '}
                          (grover)
                        </span>
                      )}
                      {st.matchLevel === 'UNRELATED' && (
                        <span style={{ color: '#B4232B', fontSize: 11.5 }}> (afwijkend)</span>
                      )}
                      {st.matchNote && (
                        <div style={{ fontSize: 11, color: theme.color.muted }}>{st.matchNote}</div>
                      )}
                    </td>
                    <td style={{ ...td, fontSize: 11.5, color: theme.color.muted }}>
                      {st.source.tableId ?? '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ margin: '10px 0 0', fontSize: 11.5, color: theme.color.muted }}>
        Bron: {branche.attribution}
        {missing > 0 && ` · ${missing} kenmerk(en) niet beschikbaar`}
        {coarser.length > 0 &&
          ` · ${coarser.length} cijfer(s) op een bredere SBI-laag dan de eigen code`}
      </p>
      <p style={{ margin: '4px 0 0', fontSize: 11.5, color: theme.color.muted }}>
        Dit zijn sectoraggregaten, geen waarden per concurrent — ze staan daarom bewust naast en
        niet in de vergelijkingstabel.
      </p>
    </div>
  );
}

/** Uitleg per weggelaten sectie. */
const SECTION_OMISSION_INTRO: Record<string, string> = {
  NO_LIVING_SOURCE: 'Geen bron in deze bronnenset',
  SOURCE_UNAVAILABLE: 'Bron kan niet leveren',
  NO_DATA_COLLECTED: 'Geen waarden in deze uitvoering',
  NOT_APPLICABLE_FOR_BRANCHE: 'Niet van toepassing voor deze branche',
  // Wezenlijk anders dan "geen waarden": daar is gezocht, hier is niets
  // geprobeerd. Dat verschil bepaalt of de lezer een bron moet aanzetten.
  CONNECTOR_DISABLED: 'Bron stond uit tijdens deze uitvoering',
};

/**
 * Weggelaten secties, zichtbaar (P11).
 *
 * Een sectie zonder mogelijke bron wordt niet leeg gerenderd — een leeg
 * schemaveld is het grootste hallucinatierisico in het systeem — maar ook niet
 * stil weggelaten, want dan leest de lezer de afwezigheid als "hier was niets te
 * melden" in plaats van "dit is niet gemeten".
 */
function OmittedSectionsCard({
  sections,
  card,
}: {
  sections: ReportSectionDto[];
  card: React.CSSProperties;
}) {
  const omitted = sections.filter((sec) => !sec.included);
  if (omitted.length === 0) return null;
  return (
    <div style={card}>
      <h2 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700 }}>Niet opgenomen secties</h2>
      <p style={{ margin: '0 0 8px', fontSize: 12, color: theme.color.muted }}>
        Deze onderdelen staan bewust niet in het rapport. Een leeg gerenderde sectie zou de
        suggestie wekken dat er iets gemeten is.
      </p>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.5 }}>
        {omitted.map((sec) => (
          <li key={sec.category}>
            <strong>{sec.labelNl}</strong>
            {sec.omissionReason && (
              <span style={{ color: theme.color.muted }}>
                {' '}
                — {SECTION_OMISSION_INTRO[sec.omissionReason] ?? sec.omissionReason}
              </span>
            )}
            {sec.messageNl && (
              <div style={{ fontSize: 11.5, color: theme.color.muted }}>{sec.messageNl}</div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Validatiebevindingen (P11). `ERROR` betekent dat er iets *niet* in het rapport
 * staat: de uitspraak is vóór weergave verwijderd. Dat hoort zichtbaar te zijn,
 * anders is het verschil tussen "de AI zei hier niets over" en "wij hebben het
 * weggehaald" onmogelijk te maken.
 */
function ValidationCard({
  findings,
  rejected,
  card,
}: {
  findings: ValidationFindingDto[];
  rejected: number;
  card: React.CSSProperties;
}) {
  if (findings.length === 0) return null;
  const errors = findings.filter((f) => f.severity === 'ERROR');
  const warnings = findings.filter((f) => f.severity === 'WARNING');
  return (
    <div
      style={{
        ...card,
        background: theme.color.warnBg,
        border: `1px solid ${theme.color.warnBorder}`,
      }}
    >
      <h2 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: theme.color.warnText }}>
        Validatie
      </h2>
      <p style={{ margin: '0 0 8px', fontSize: 12, color: theme.color.warnText }}>
        Deterministische controle vóór weergave: elke uitspraak moet naar een bestaande observatie
        verwijzen, elk getal moet uit die observatie volgen en er wordt geen rangorde uitgesproken
        op een dimensie met gaten.
        {rejected > 0 && ` ${rejected} uitspraak/uitspraken zijn hierom niet weergegeven.`}
      </p>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: theme.color.warnText }}>
        {[...errors, ...warnings].map((f, i) => (
          <li key={`${f.code}-${i}`} style={{ marginBottom: 3 }}>
            <strong>{f.severity === 'ERROR' ? 'Verwijderd' : 'Kanttekening'}:</strong> {f.messageNl}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Handmatige correctie van één cel (P12).
 *
 * De API hiervoor bestond al sinds M21, maar `apps/web/src/api.ts` had er geen
 * enkele functie voor (bevinding K): een analist kon een fout getal wél zien en
 * niet corrigeren, en moest dus buiten het systeem om.
 *
 * Twee dingen zijn met opzet verplicht. **De reden is een verplicht veld** —
 * een correctie zonder reden is over een half jaar niet meer te beoordelen, en
 * het is precies wat er in de export moet staan. En **de bronobservatie blijft
 * bestaan**: de correctie ligt eroverheen en is intrekbaar, zodat er niets
 * stilzwijgend wordt overschreven.
 */
function CorrectionForm({
  runId,
  row,
  subject,
  cell,
  onDone,
  onCancel,
}: {
  runId: string;
  row: ComparisonRow;
  subject: ComparisonResponse['subjects'][number];
  cell: ComparisonCell | undefined;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [protectedFromRegeneration, setProtected] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isList = row.valueType === 'LIST';
  const isNumber = row.valueType === 'NUMBER';
  const isBoolean = row.valueType === 'BOOLEAN';

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    // Het waardetype van de metriek bepaalt welk veld gevuld wordt; een getal
    // als tekst opsturen zou door de normalisatie geweigerd worden.
    let payloadValue: Record<string, unknown>;
    if (isNumber) {
      const parsed = Number(value.replace(',', '.'));
      if (!Number.isFinite(parsed)) {
        setError('Vul een geldig getal in.');
        return;
      }
      payloadValue = { number: parsed, unit: row.unit ?? undefined };
    } else if (isBoolean) {
      payloadValue = { boolean: value === 'ja' };
    } else if (isList) {
      payloadValue = {
        list: value
          .split(/[\n,;]/)
          .map((part) => part.trim())
          .filter(Boolean),
      };
    } else {
      payloadValue = { text: value.trim() };
    }

    setBusy(true);
    try {
      await createCorrection(runId, {
        subjectType: subject.subjectType,
        subjectId: subject.subjectId,
        metricCode: row.metricCode,
        value: payloadValue,
        reason: reason.trim(),
        protectedFromRegeneration,
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'De correctie kon niet worden opgeslagen.');
    } finally {
      setBusy(false);
    }
  }

  const input = {
    width: '100%',
    padding: '7px 9px',
    borderRadius: 7,
    border: `1px solid ${theme.color.borderStrong}`,
    fontSize: 13,
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
  };

  return (
    <form
      onSubmit={(event) => void submit(event)}
      style={{
        marginTop: 8,
        padding: 12,
        borderRadius: 10,
        background: theme.color.accentBg,
        border: `1px solid ${theme.color.border}`,
      }}
    >
      <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 8 }}>
        {row.nameNl} — {subject.name}
      </div>
      <div style={{ fontSize: 11.5, color: theme.color.muted, marginBottom: 8 }}>
        Huidige waarde: {cell?.displayText ?? '—'}. De bronobservatie blijft bewaard; de correctie
        komt eroverheen en is intrekbaar.
      </div>

      <label style={{ display: 'block', fontSize: 12, marginBottom: 3 }}>
        Nieuwe waarde{row.unit ? ` (${row.unit})` : ''}
      </label>
      {isBoolean ? (
        <select value={value} onChange={(e) => setValue(e.target.value)} style={input} required>
          <option value="">Kies…</option>
          <option value="ja">Ja</option>
          <option value="nee">Nee</option>
        </select>
      ) : isList ? (
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={3}
          placeholder="Eén per regel, of gescheiden door komma's"
          style={input}
          required
        />
      ) : (
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          inputMode={isNumber ? 'decimal' : 'text'}
          style={input}
          required
        />
      )}

      <label style={{ display: 'block', fontSize: 12, margin: '10px 0 3px' }}>
        Reden <span style={{ color: theme.color.muted }}>(verplicht)</span>
      </label>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
        maxLength={1000}
        placeholder="Waarom is de bronwaarde onjuist?"
        style={input}
        required
      />

      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 8 }}>
        <input
          type="checkbox"
          checked={protectedFromRegeneration}
          onChange={(e) => setProtected(e.target.checked)}
        />
        Beschermen tegen regeneratie
        <span style={{ color: theme.color.muted }}>
          — blijft staan als het rapport opnieuw wordt opgebouwd
        </span>
      </label>

      {error && (
        <p style={{ margin: '8px 0 0', fontSize: 12, color: theme.color.warnText }}>{error}</p>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button
          type="submit"
          disabled={busy}
          style={{
            background: theme.color.primary,
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '7px 14px',
            fontSize: 13,
            fontWeight: 600,
            cursor: busy ? 'wait' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {busy ? 'Opslaan…' : 'Correctie opslaan'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{
            background: theme.color.surface,
            border: `1px solid ${theme.color.borderStrong}`,
            borderRadius: 8,
            padding: '7px 14px',
            fontSize: 13,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Annuleren
        </button>
      </div>
    </form>
  );
}

/** Overzicht van de actieve correcties, met de mogelijkheid ze in te trekken. */
function CorrectionsCard({
  runId,
  corrections,
  subjects,
  rows,
  onChanged,
  card,
}: {
  runId: string;
  corrections: ManualCorrectionDto[];
  subjects: ComparisonResponse['subjects'];
  rows: ComparisonRow[];
  onChanged: () => void;
  card: React.CSSProperties;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);

  async function withdraw(id: string) {
    setBusyId(id);
    try {
      await withdrawCorrection(runId, id);
      onChanged();
    } catch {
      // Stil falen is hier acceptabel: de lijst wordt hoe dan ook herladen, en
      // een mislukte intrekking laat de correctie simpelweg staan.
      onChanged();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div style={card}>
      <h2 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700 }}>Handmatige correcties</h2>
      <p style={{ margin: '0 0 10px', fontSize: 12, color: theme.color.muted }}>
        Een correctie vervangt de weergegeven waarde maar overschrijft de bronobservatie niet. Klik
        op ✎ bij een cel in de vergelijking om er een toe te voegen.
      </p>
      {corrections.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13.5, color: theme.color.muted }}>
          Er zijn geen handmatige correcties voor deze uitvoering.
        </p>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.5 }}>
          {corrections.map((correction) => {
            const subject = subjects.find(
              (s) =>
                s.subjectId === correction.subjectId && s.subjectType === correction.subjectType,
            );
            const metric = rows.find((r) => r.metricCode === correction.metricCode);
            return (
              <li key={correction.id} style={{ marginBottom: 6 }}>
                <strong>{metric?.nameNl ?? correction.metricCode}</strong>
                {subject ? ` — ${subject.name}` : ''}:{' '}
                {formatCellText({
                  availability: 'AVAILABLE',
                  provenanceTier: null,
                  value: correction.value,
                })}
                <div style={{ fontSize: 11.5, color: theme.color.muted }}>
                  {correction.reason}
                  {correction.protectedFromRegeneration ? ' · beschermd tegen regeneratie' : ''}
                </div>
                <button
                  type="button"
                  onClick={() => void withdraw(correction.id)}
                  disabled={busyId === correction.id}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    marginTop: 2,
                    fontSize: 11.5,
                    color: theme.color.primary,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {busyId === correction.id ? 'Intrekken…' : 'Intrekken'}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Kleur per connectorstatus voor de bronnenweergave. */
function connectorStatusColor(status: string): string {
  if (status === 'SUCCESS') return '#1E7A44';
  if (status === 'PARTIAL') return '#8A5A00';
  if (status === 'FAILED') return '#B4232B';
  return theme.color.muted; // SKIPPED/CANCELLED/PENDING/RUNNING
}

const AI_ACCENT = '#7c3aed';

/**
 * Verwijzing van één claim naar de observaties waarop hij steunt (P11).
 *
 * De synthese slaat per uitspraak `evidenceIds` op, maar tot nu toe werd daar
 * niets van getoond: de lezer zag een AI-bewering zonder te kunnen zien waarop
 * ze steunde, terwijl de herleidbaarheid wél in de database stond. Onbekende
 * ID's komen hier niet voor — die zijn door de validator al uit de claim
 * verwijderd, en de claim zelf is dan niet weergegeven.
 */
function EvidenceRefs({
  claim,
  labelFor,
}: {
  claim: SynthesisClaimDto;
  labelFor: (observationId: string) => string | null;
}) {
  const labels = claim.evidenceIds.map(labelFor).filter((l): l is string => l !== null);
  if (labels.length === 0) return null;
  return (
    <span style={{ fontSize: 11.5, color: theme.color.muted }}>
      {' '}
      — op basis van {labels.join('; ')}
    </span>
  );
}

/** Toont de AI-conceptsynthese, duidelijk gelabeld en gescheiden van de feiten. */
function AiSynthesisCard({
  synthesis,
  card,
  labelFor,
}: {
  synthesis: ReportSynthesisResponse;
  card: React.CSSProperties;
  labelFor: (observationId: string) => string | null;
}) {
  const sections: { label: string; items: SynthesisClaimDto[] }[] = [
    { label: 'Sterktes', items: synthesis.strengths },
    { label: 'Zwaktes', items: synthesis.weaknesses },
    { label: 'Kansen', items: synthesis.opportunities },
    { label: 'Bedreigingen', items: synthesis.threats },
    { label: 'Aanbevelingen', items: synthesis.recommendations },
  ];
  const generated = synthesis.generatedAt
    ? new Intl.DateTimeFormat('nl-NL', { dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(synthesis.generatedAt),
      )
    : null;
  const totalTokens =
    synthesis.promptTokens !== null && synthesis.completionTokens !== null
      ? synthesis.promptTokens + synthesis.completionTokens
      : null;
  const tokenText =
    totalTokens !== null
      ? `${new Intl.NumberFormat('nl-NL').format(totalTokens)} tokens (prompt ${new Intl.NumberFormat('nl-NL').format(synthesis.promptTokens ?? 0)} · antwoord ${new Intl.NumberFormat('nl-NL').format(synthesis.completionTokens ?? 0)})`
      : null;
  const costText =
    synthesis.costEur !== null
      ? new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(
          synthesis.costEur,
        )
      : null;

  return (
    <div style={{ ...card, borderLeft: `4px solid ${AI_ACCENT}` }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          marginBottom: 4,
          flexWrap: 'wrap',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>AI-samenvatting</h2>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: '#fff',
            background: AI_ACCENT,
            borderRadius: 6,
            padding: '2px 7px',
          }}
        >
          AI
        </span>
        <span style={{ fontSize: 11.5, color: theme.color.muted }}>
          concept · geen feiten{synthesis.model ? ` · ${synthesis.model}` : ''}
          {generated ? ` · ${generated}` : ''}
        </span>
      </div>
      <p style={{ margin: '0 0 6px', fontSize: 12, color: theme.color.muted }}>
        Door AI samengevat uit de onderstaande observaties; controleer altijd zelf. Ontbrekende
        gegevens blijven onbekend en worden nooit ingevuld.
      </p>
      {(tokenText || costText) && (
        <p style={{ margin: '0 0 12px', fontSize: 11.5, color: theme.color.muted }}>
          Verbruik: {tokenText ?? 'onbekend'}
          {costText ? ` · geschatte kosten ${costText}` : ''}
        </p>
      )}

      {synthesis.managementSummary && (
        <p style={{ margin: '0 0 14px', fontSize: 13.5, lineHeight: 1.5 }}>
          {synthesis.managementSummary}
        </p>
      )}

      {sections
        .filter((s) => s.items.length > 0)
        .map((s) => (
          <div key={s.label} style={{ marginBottom: 10 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700 }}>{s.label}</h3>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.45 }}>
              {s.items.map((item, i) => (
                <li key={i}>
                  {item.text}
                  <EvidenceRefs claim={item} labelFor={labelFor} />
                </li>
              ))}
            </ul>
          </div>
        ))}

      {synthesis.droppedClaims !== null && synthesis.droppedClaims > 0 && (
        <p style={{ margin: '8px 0 0', fontSize: 11.5, color: theme.color.muted }}>
          {synthesis.droppedClaims} ongefundeerde uitspraak/uitspraken zonder geldige bron zijn
          weggelaten.
        </p>
      )}
    </div>
  );
}

type ReportTab = 'samenvatting' | 'vergelijking' | 'bronnen';

export function ReportView({ runId }: { runId: string }) {
  const [state, setState] = useState<
    | { status: 'loading' }
    | {
        status: 'ready';
        comparison: ComparisonResponse;
        sources: SourcesResponse;
        changes: RunChangesResponse | null;
        synthesis: ReportSynthesisResponse | null;
        branche: BrancheResponse | null;
      }
    | { status: 'error' }
  >({ status: 'loading' });
  const [tab, setTab] = useState<ReportTab>('samenvatting');
  const [shared, setShared] = useState(false);
  const [corrections, setCorrections] = useState<ManualCorrectionDto[]>([]);
  /** `metricCode|subjectKey` van de cel waarvan het correctieformulier open staat. */
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [canCorrect, setCanCorrect] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [comparison, sources] = await Promise.all([
          fetchComparison(runId),
          fetchSources(runId),
        ]);
        // Wijzigingsvergelijking, AI-synthese en marktkader zijn optioneel: een
        // ontbrekend marktkader mag het rapport niet blokkeren, want de
        // vergelijking zelf staat er los van.
        const [changes, synthesis, branche] = await Promise.all([
          fetchRunChanges(runId).catch(() => null),
          fetchSynthesis(runId).catch(() => null),
          fetchBranche(runId).catch(() => null),
        ]);
        if (!cancelled)
          setState({ status: 'ready', comparison, sources, changes, synthesis, branche });
        // Correcties apart: de rest van het rapport mag niet wachten op een
        // lijst die vaak leeg is.
        const correctionList = await fetchCorrections(runId).catch(() => null);
        if (!cancelled && correctionList) setCorrections(correctionList.corrections);
        // Alleen Analist/Beheerder mag corrigeren; een Kijker ziet de knop niet.
        const me = await fetchAuthMe().catch(() => null);
        if (!cancelled) {
          setCanCorrect(me?.user?.role === 'ANALYST' || me?.user?.role === 'ADMIN');
        }
      } catch {
        if (!cancelled) setState({ status: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runId]);

  const card = {
    background: theme.color.surface,
    border: `1px solid ${theme.color.border}`,
    borderRadius: 14,
    padding: '22px 26px',
    marginBottom: 16,
  } as const;

  if (state.status === 'loading') {
    return (
      <div style={card}>
        <p style={{ margin: 0, color: theme.color.muted, fontSize: 13.5 }}>Rapport laden…</p>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div style={card}>
        <p style={{ margin: 0, color: theme.color.muted, fontSize: 13.5 }}>
          Het rapport kon niet worden geladen.
        </p>
      </div>
    );
  }

  const { comparison, sources, changes, synthesis, branche } = state;

  /**
   * Herlaadt na een correctie. De vergelijking wordt bij het *lezen* berekend
   * (PLAN.md §13.1), dus een correctie is meteen zichtbaar zodra de comparison
   * opnieuw is opgehaald — er is geen rapport dat geïnvalideerd moet worden.
   */
  async function reloadCorrections(): Promise<void> {
    const [comparisonNext, correctionList] = await Promise.all([
      fetchComparison(runId).catch(() => null),
      fetchCorrections(runId).catch(() => null),
    ]);
    if (correctionList) setCorrections(correctionList.corrections);
    if (comparisonNext) {
      setState((current) =>
        current.status === 'ready' ? { ...current, comparison: comparisonNext } : current,
      );
    }
  }
  const categories = [...new Set(comparison.rows.map((r) => r.category))];

  // Observatie-ID → leesbaar label, voor de bronvermelding onder de AI-claims.
  const evidenceLabels = new Map<string, string>();
  for (const row of comparison.rows) {
    for (const cell of row.cells) {
      if (!cell.observationId) continue;
      const subject = comparison.subjects.find((sub) => sub.key === cell.subjectKey);
      evidenceLabels.set(
        cell.observationId,
        subject ? `${row.nameNl} (${subject.name})` : row.nameNl,
      );
    }
  }
  // P15 — de synthese mag ook marktcijfers citeren. Zonder deze regels valt zo'n
  // verwijzing stil weg in `EvidenceRefs` en lijkt de claim onderbouwd door
  // niets, terwijl de onderbouwing er juist is.
  for (const st of branche?.statistics ?? []) {
    evidenceLabels.set(st.id, `${st.nameNl} (marktkader CBS)`);
  }
  const labelForEvidence = (observationId: string): string | null =>
    evidenceLabels.get(observationId) ?? null;
  const srcCount = (st: string) => sources.sources.filter((s) => s.status === st).length;
  const srcStats = {
    total: sources.sources.length,
    ok: srcCount('SUCCESS'),
    partial: srcCount('PARTIAL'),
    failed: srcCount('FAILED'),
    skipped: srcCount('SKIPPED') + srcCount('CANCELLED'),
  };
  const failedSources = sources.sources.filter((s) =>
    ['FAILED', 'SKIPPED', 'CANCELLED'].includes(s.status),
  );
  const limitations: string[] = [];
  if (sources.failureSummary) limitations.push(sources.failureSummary);
  if (failedSources.length > 0) {
    limitations.push(
      `${failedSources.length} bronaanroep${failedSources.length === 1 ? '' : 'en'} zonder resultaat (zie bronnenoverzicht); de bijbehorende waarden zijn "Niet beschikbaar".`,
    );
  }
  if (comparison.rows.length === 0) {
    limitations.push(
      'Er zijn nog geen observaties verzameld. Controleer of er connectors zijn ingeschakeld (feature flag) en geconfigureerd.',
    );
  }
  const incompleteRows = comparison.rows.filter((r) => !r.completeness.rankable);
  if (incompleteRows.length > 0) {
    limitations.push(
      `${incompleteRows.length} van de ${comparison.rows.length} dimensies zijn niet voor alle subjecten bekend; op die dimensies staat geen rangorde.`,
    );
  }
  const validationFindings: ValidationFindingDto[] = [
    ...comparison.validation,
    ...(synthesis?.validation ?? []),
  ];

  const exportBtn = {
    background: theme.color.surface,
    border: `1px solid ${theme.color.borderStrong}`,
    borderRadius: 8,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 500,
    color: theme.color.text,
    textDecoration: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'inline-block',
  } as const;

  async function onShare() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShared(true);
      setTimeout(() => setShared(false), 2500);
    } catch {
      setShared(false);
    }
  }

  const TABS: { key: ReportTab; label: string }[] = [
    { key: 'samenvatting', label: 'Samenvatting' },
    { key: 'vergelijking', label: 'Vergelijking' },
    { key: 'bronnen', label: 'Bronnen & betrouwbaarheid' },
  ];

  return (
    <>
      {/* Toolbar: export en delen (conform prototype). "Opnieuw uitvoeren" staat
          in de kop van het analysescherm. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <a href={`/api/analysis-runs/${encodeURIComponent(runId)}/export.pdf`} style={exportBtn}>
          Exporteer PDF
        </a>
        <a href={`/api/analysis-runs/${encodeURIComponent(runId)}/export.csv`} style={exportBtn}>
          Exporteer Excel (CSV)
        </a>
        <button type="button" onClick={() => void onShare()} style={exportBtn}>
          {shared ? 'Link gekopieerd ✓' : 'Delen'}
        </button>
      </div>

      {/* Legenda */}
      <div
        style={{
          ...card,
          marginBottom: 18,
          padding: '11px 16px',
          display: 'flex',
          gap: 18,
          alignItems: 'center',
          flexWrap: 'wrap',
          fontSize: 12,
        }}
      >
        <span
          style={{
            fontWeight: 600,
            color: theme.color.muted,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            fontSize: 11,
          }}
        >
          Legenda
        </span>
        <span>
          <span
            style={{
              background: theme.color.accentBg,
              color: theme.color.primary,
              borderRadius: 9,
              padding: '2px 8px',
              fontWeight: 600,
              fontSize: 10.5,
            }}
          >
            FEIT
          </span>{' '}
          vastgesteld uit bron
        </span>
        <span>
          <span
            style={{
              background: theme.color.warnBg,
              color: '#8A6A12',
              borderRadius: 9,
              padding: '2px 8px',
              fontWeight: 600,
              fontSize: 10.5,
            }}
          >
            SCHATTING
          </span>{' '}
          afgeleid, met bandbreedte
        </span>
        <span>
          <span
            style={{
              background: '#F0E8F5',
              color: '#6B3E8F',
              borderRadius: 9,
              padding: '2px 8px',
              fontWeight: 600,
              fontSize: 10.5,
            }}
          >
            AI
          </span>{' '}
          gegenereerde interpretatie of aanbeveling
        </span>
        <span style={{ borderLeft: `1px solid ${theme.color.border}`, paddingLeft: 18 }}>
          Betrouwbaarheid: <span style={{ color: '#157A4A', fontWeight: 600 }}>● hoog</span>{' '}
          <span style={{ color: '#8A6A12', fontWeight: 600 }}>● middel</span>{' '}
          <span style={{ color: '#A33B2E', fontWeight: 600 }}>● laag</span>
        </span>
      </div>

      {/* Marktkader: bovenaan het rapport, vóór de concurrentvergelijking, en
          buiten de tabs — het is de context waarin álle tabbladen gelezen moeten
          worden (P11, PLAN.md §11). */}
      {branche && <MarketFrameCard branche={branche} card={card} />}

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: `2px solid ${theme.color.border}`,
          marginBottom: 22,
        }}
      >
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              style={{
                background: 'none',
                border: 'none',
                borderBottom: `2.5px solid ${active ? theme.color.primary : 'transparent'}`,
                marginBottom: -2,
                padding: '10px 16px',
                fontSize: 14,
                fontWeight: active ? 700 : 500,
                color: active ? theme.color.text : theme.color.muted,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ── Tab: Samenvatting ── */}
      {tab === 'samenvatting' &&
        (synthesis?.available ? (
          <AiSynthesisCard synthesis={synthesis} card={card} labelFor={labelForEvidence} />
        ) : (
          <div style={card}>
            <h2 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700 }}>
              Managementsamenvatting
            </h2>
            <p style={{ margin: 0, fontSize: 13.5, color: theme.color.muted }}>
              Er is (nog) geen AI-samenvatting voor deze uitvoering. De feitelijke vergelijking
              staat onder het tabblad <strong>Vergelijking</strong>.
            </p>
          </div>
        ))}
      {tab === 'samenvatting' && (
        <ValidationCard
          findings={validationFindings}
          rejected={synthesis?.rejectedByValidator ?? 0}
          card={card}
        />
      )}

      {/* ── Tab: Vergelijking ── */}
      {tab === 'vergelijking' && (
        <>
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Vergelijking</h2>
              <span style={{ fontSize: 11.5, color: theme.color.muted }}>
                deterministisch · zonder AI-conclusies · regelversie{' '}
                {comparison.comparisonRuleVersion}
              </span>
            </div>
            {comparison.rows.length === 0 ? (
              <p style={{ margin: '10px 0 0', fontSize: 13.5, color: theme.color.muted }}>
                Nog geen vergelijkingsdata beschikbaar.
              </p>
            ) : (
              categories.map((category) => (
                <div key={category} style={{ marginTop: 18 }}>
                  <h3
                    style={{
                      margin: '0 0 6px',
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: theme.color.primaryDark,
                    }}
                  >
                    {METRIC_CATEGORY_LABELS[category as keyof typeof METRIC_CATEGORY_LABELS] ??
                      category}
                  </h3>
                  <ComparisonTable
                    rows={comparison.rows.filter((r) => r.category === category)}
                    subjects={comparison.subjects}
                    coverage={comparison.coverage}
                    canCorrect={canCorrect}
                    editing={editingCell}
                    onEdit={setEditingCell}
                    runId={runId}
                    onCorrected={() => void reloadCorrections()}
                  />
                </div>
              ))
            )}
          </div>

          {/* Handmatige correcties (P12) */}
          <CorrectionsCard
            runId={runId}
            corrections={corrections}
            subjects={comparison.subjects}
            rows={comparison.rows}
            onChanged={() => void reloadCorrections()}
            card={card}
          />

          {/* Zichtbaar weggelaten secties (P11) */}
          <OmittedSectionsCard sections={comparison.sections} card={card} />

          {/* Bronconflicten (M18/US06) */}
          {comparison.conflicts.length > 0 && (
            <div style={card}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
                <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Bronconflicten</h2>
                <span style={{ fontSize: 11.5, color: theme.color.muted }}>
                  tegenstrijdige waarden tussen bronnen · handmatige controle aanbevolen
                </span>
              </div>
              <ul
                style={{
                  margin: '10px 0 0',
                  paddingLeft: 18,
                  fontSize: 13,
                  color: theme.color.text,
                }}
              >
                {comparison.conflicts.map((c) => {
                  const subject = comparison.subjects.find(
                    (s) => s.subjectId === c.subjectId && s.subjectType === c.subjectType,
                  );
                  const metric = comparison.rows.find((r) => r.metricCode === c.metricCode);
                  const sev =
                    c.severity === 'HIGH' ? 'Hoog' : c.severity === 'LOW' ? 'Laag' : 'Middel';
                  return (
                    <li
                      key={`${c.metricCode}:${c.subjectType}:${c.subjectId}`}
                      style={{ marginBottom: 4 }}
                    >
                      <strong>{metric?.nameNl ?? c.metricCode}</strong>
                      {subject ? ` — ${subject.name}` : ''}: {c.description}{' '}
                      <span style={{ color: theme.color.muted, fontSize: 11.5 }}>
                        (ernst: {sev})
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}

      {/* ── Tab: Bronnen & betrouwbaarheid ── */}
      {tab === 'bronnen' && (
        <>
          <div style={card}>
            <h2 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700 }}>
              Bronnen en actualiteit
            </h2>
            {srcStats.total > 0 && (
              <p style={{ margin: '2px 0 0', fontSize: 12.5, color: theme.color.muted }}>
                <strong style={{ color: connectorStatusColor('SUCCESS') }}>{srcStats.ok}</strong>{' '}
                van {srcStats.total} bronaanroepen gelukt
                {srcStats.partial > 0 && ` · ${srcStats.partial} gedeeltelijk`}
                {srcStats.failed > 0 && (
                  <>
                    {' · '}
                    <strong style={{ color: connectorStatusColor('FAILED') }}>
                      {srcStats.failed} mislukt
                    </strong>
                  </>
                )}
                {srcStats.skipped > 0 && ` · ${srcStats.skipped} overgeslagen`}
              </p>
            )}
            {sources.sources.length === 0 ? (
              <p style={{ margin: '10px 0 0', fontSize: 13.5, color: theme.color.muted }}>
                Er zijn geen bronaanroepen geregistreerd voor deze uitvoering.
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                  <thead>
                    <tr>
                      {['Bron', 'Subject', 'Status', 'Opgehaald', 'Versie'].map((h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: 'left',
                            padding: '8px 12px',
                            borderBottom: `1px solid ${theme.color.border}`,
                            fontSize: 12,
                            color: theme.color.muted,
                            fontWeight: 600,
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sources.sources.map((s) => (
                      <tr key={s.id}>
                        <td
                          style={{
                            padding: '8px 12px',
                            borderBottom: `1px solid ${theme.color.border}`,
                            fontSize: 13,
                          }}
                        >
                          {s.connectorName}
                        </td>
                        <td
                          style={{
                            padding: '8px 12px',
                            borderBottom: `1px solid ${theme.color.border}`,
                            fontSize: 13,
                          }}
                        >
                          {s.subjectName}
                        </td>
                        <td
                          style={{
                            padding: '8px 12px',
                            borderBottom: `1px solid ${theme.color.border}`,
                            fontSize: 13,
                          }}
                        >
                          <span style={{ color: connectorStatusColor(s.status), fontWeight: 600 }}>
                            {CONNECTOR_RUN_STATUS_LABELS[s.status] ?? s.status}
                          </span>
                          {s.servedFromCache && (
                            <span
                              title="Hergebruikt uit een recente check van hetzelfde domein in een ander project (geen nieuwe API-aanroep). De ophaaldatum toont wanneer de data oorspronkelijk is opgehaald."
                              style={{
                                marginLeft: 6,
                                fontSize: 11,
                                fontWeight: 600,
                                color: theme.color.muted,
                                border: `1px solid ${theme.color.border}`,
                                borderRadius: 6,
                                padding: '1px 6px',
                              }}
                            >
                              ♻ uit cache
                            </span>
                          )}
                          {s.errorCode && (
                            <span
                              title={s.errorMessage ?? undefined}
                              style={{ color: theme.color.warnText, fontSize: 11.5 }}
                            >
                              {' '}
                              ({s.errorCode})
                            </span>
                          )}
                          {s.errorMessage && (
                            <div style={{ fontSize: 11, color: theme.color.muted, marginTop: 2 }}>
                              {s.errorMessage}
                            </div>
                          )}
                        </td>
                        <td
                          style={{
                            padding: '8px 12px',
                            borderBottom: `1px solid ${theme.color.border}`,
                            fontSize: 13,
                          }}
                        >
                          {formatDate(s.retrievedAt ?? s.finishedAt)}
                        </td>
                        <td
                          style={{
                            padding: '8px 12px',
                            borderBottom: `1px solid ${theme.color.border}`,
                            fontSize: 13,
                          }}
                        >
                          {s.connectorVersion ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Wijzigingen t.o.v. de vorige uitvoering (M24) */}
          {changes && changes.hasBaseline && (
            <div style={card}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
                <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
                  Wijzigingen t.o.v. vorige uitvoering
                </h2>
                <span style={{ fontSize: 11.5, color: theme.color.muted }}>
                  run {changes.baselineRunNumber} → {changes.currentRunNumber} · regelversie{' '}
                  {changes.comparisonRuleVersion}
                </span>
              </div>
              {changes.observationChanges.length === 0 &&
              changes.competitorChanges.every((c) => c.changeType === 'UNCHANGED') ? (
                <p style={{ margin: 0, fontSize: 13.5, color: theme.color.muted }}>
                  Geen noemenswaardige wijzigingen ten opzichte van de vorige uitvoering.
                </p>
              ) : (
                <>
                  {changes.competitorChanges.some((c) => c.changeType !== 'UNCHANGED') && (
                    <p style={{ margin: '0 0 8px', fontSize: 13 }}>
                      Concurrenten:{' '}
                      {changes.competitorChanges
                        .filter((c) => c.changeType !== 'UNCHANGED')
                        .map(
                          (c) =>
                            `${c.name} (${c.changeType === 'ADDED' ? 'toegevoegd' : 'verwijderd'})`,
                        )
                        .join(', ')}
                    </p>
                  )}
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                    {changes.observationChanges.map((c) => (
                      <li key={`${c.subjectId}-${c.metricCode}`} style={{ marginBottom: 3 }}>
                        <strong>{c.metricNameNl}</strong>
                        {c.oldText !== null && c.newText !== null
                          ? `: ${c.oldText} → ${c.newText}`
                          : ''}{' '}
                        <span style={{ color: theme.color.muted }}>— {c.explanation}</span>
                        {c.significance === 'MAJOR' && (
                          <span style={{ color: theme.color.warnText, fontWeight: 600 }}>
                            {' '}
                            (opvallend)
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          {/* Beperkingen */}
          {limitations.length > 0 && (
            <div
              style={{
                ...card,
                background: theme.color.warnBg,
                border: `1px solid ${theme.color.warnBorder}`,
              }}
            >
              <h2
                style={{
                  margin: '0 0 8px',
                  fontSize: 15,
                  fontWeight: 700,
                  color: theme.color.warnText,
                }}
              >
                Beperkingen
              </h2>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: theme.color.warnText }}>
                {limitations.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </>
  );
}
